import {
	DrawerContentScrollView,
	DrawerItem,
	createDrawerNavigator,
} from '@react-navigation/drawer';
import { View } from 'react-native';
import { appConfigStore } from '../../../app/stores/AppConfig';
import { navigationStore } from '../../../app/stores/Navigation';
import { Header } from '../../Header';
import { useScreenComponent } from '../../../app/hooks/custom/useScreenComponent';
import { CartIcon } from '../../CartIcon';
import { ProfileIcon } from '../../ProfileIcon';
import { userStore } from '../../../app/stores/User';
import { ThemeToggle } from '../../ThemeToggle';
import { Box, Typography, useTheme } from '@mui/material';
import { AppLogo } from '../../AppLogo';
import { useStoreConfig } from '../../../contexts/StoreConfigContext';
import { useComponentMount } from '../../../services/InitializationService';
import { ScreenComponent } from '../../../models/enums/ScreenComponents';

const Drawer = createDrawerNavigator();

// Screen components that are handled via profile drawer, not as standalone screens
const drawerOnlyScreens = [ScreenComponent.Login, ScreenComponent.Register];

// Filter out any invalid/removed screen components from persisted storage
const validScreenComponents = Object.values(ScreenComponent).filter(
	screen => !drawerOnlyScreens.includes(screen)
);

export const DrawerNavigation = () => {
	useComponentMount('DrawerNavigation');
	const theme = useTheme();
	const { config } = useStoreConfig();
	const {
		splashLoaded,
		currentScreen,
		screenList,
		drawerItemList,
		screenDisplayNames,
	} = navigationStore((state) => state);

	// Filter to only include valid screen components
	const validScreenList = screenList.filter(screen => validScreenComponents.includes(screen));
	const validDrawerItemList = drawerItemList.filter(screen => validScreenComponents.includes(screen));
	const validCurrentScreen = validScreenComponents.includes(currentScreen) 
		? currentScreen 
		: ScreenComponent.Shop;

	const { getScreenComponent, navigateToScreen } = useScreenComponent();
	const { showNavigationBar } = navigationStore((state) => state);
	const { darkMode } = appConfigStore((state) => state);
	const { info, authenticated, role } = userStore((state) => state.user);

	// Use Google Font if set, otherwise use local font
	const primaryFontFamily = config.primaryFont || 'primaryFont';

	const CustomDrawerContent = (props) => {
		return (
			<View style={{ flex: 1 }}>
				<DrawerContentScrollView {...props}>
					<Header enableDivider={false} showLogoText={true} />

					{
						validDrawerItemList.map((screen, index) => {
							const isCurrentScreen = validCurrentScreen === screen;
							const displayName = screenDisplayNames[screen] || screen.replaceAll('_', ' ');

							return (
								<DrawerItem
									key={'item_' + index}
									label={displayName}
									labelStyle={{
										paddingHorizontal: 16,
										fontFamily: primaryFontFamily,
										fontSize: 16,
										fontWeight: '600',
										color: isCurrentScreen
											? 'white'
											: theme.palette.text.primary
									}}
									style={{
										backgroundColor: isCurrentScreen
											? config.primaryColor
											: theme.palette.background.paper
									}}
									onPress={() => navigateToScreen(screen)}
								/>
							);
						})
					}
				</DrawerContentScrollView>

				{/* Bottom Panel with Theme Toggle - only show if theme is dynamic */}
				{config.themeMode !== 'dark' && config.themeMode !== 'light' && (
					<Box sx={{
						borderTopWidth: 1,
						borderTopStyle: "solid",
						borderTopColor: "divider",
						paddingX: 2.5,
						paddingY: 2,
						backgroundColor: theme.palette.background.paper,
					}}>
						<ThemeToggle variant="drawer" />
					</Box>
				)}
			</View>
		);
	};

	return (
		<Drawer.Navigator
			useLegacyImplementation
			defaultStatus="closed"
			initialRouteName={validCurrentScreen.toString()}
			drawerContent={(props) => <CustomDrawerContent {...props} />}
			screenOptions={{
				drawerType: "slide",
				headerShown: (!splashLoaded || !showNavigationBar) ? false : true,
				headerTintColor: theme.palette.text.primary,
				drawerStyle: {
					width: "75%",
					backgroundColor: theme.palette.background.paper,
				},
				headerStyle: {
					minHeight: 85,
					backgroundColor: theme.palette.background.paper,
					borderBottomWidth: 0,
					// @ts-ignore
					boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
				},
				headerTitle: ({ children }) => (
					<View style={{ flexDirection: "row", alignItems: "center" }}>
						<AppLogo showText={false} size={50} sx={{ marginTop: 0, marginRight: 1.5, marginLeft: -1.5 }} />
						<Typography
							variant="subtitle1"
							sx={{
								color: theme.palette.text.primary,
								fontWeight: 600
							}}
							textAlign="center"
						>
							{children}
						</Typography>
					</View>
				),
				headerRight: () => {
					return (
						<View style={{ flexDirection: 'row', marginRight: 7, gap: 14 }}>
							<ProfileIcon
								isAuthenticated={authenticated}
								firstName={info?.user_metadata?.firstName ?? ''}
								darkMode={darkMode}
								role={role}
							/>
							<CartIcon />
						</View>
					)
				},
			}}
		>
			{
				validScreenList.map((screen, index) => {
				const ScreenComp = getScreenComponent(screen);
				// Skip rendering if component is undefined (safety check)
				if (!ScreenComp) {
					console.warn(`[DrawerNavigation] Skipping undefined screen: ${screen}`);
					return null;
				}
				const displayName = screenDisplayNames[screen] || screen.replaceAll('_', ' ').toString();
				return (
					<Drawer.Screen
						key={'screen_' + index}
						name={screen.toString()}
						component={ScreenComp}
						options={{
							title: displayName,
							drawerLabel: displayName,
						}}
					/>
				);
			}).filter(Boolean)
		}
		</Drawer.Navigator>
	);
};
